import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AsyncCombobox } from "@/components/app/AsyncCombobox";
import { Gift } from "lucide-react";
import { TipLibrary } from "@/components/welcome/TipLibrary";
import { TipInsights } from "@/components/welcome/TipInsights";
import { LabelBranding } from "@/components/welcome/LabelBranding";
import { BrandingReviews } from "@/components/welcome/BrandingReviews";
import { LabelContent } from "@/components/welcome/LabelContent";
import { SubsidiaryPicker } from "@/components/welcome/LabelBranding";
import { PromotionsTab } from "@/components/welcome/PromotionsTab";
import { ExperienceSwitches } from "@/components/welcome/ExperienceSwitches";
import { DirectSaleLabels } from "@/components/welcome/DirectSaleLabels";
import { EmployeeVoice } from "@/components/welcome/EmployeeVoice";

const searchCustomers = async (q) => {
  const { data } = await api.get("/master-companies", { params: { q, limit: 25 } });
  return (data.items || []).map((c) => ({ value: c.id, label: c.name || c.legal_name || c.id, sub: [c.code, c.country].filter(Boolean).join(" · ") }));
};

function BrandingTab({ onCount }) {
  const [cid, setCid] = useState("");
  const [label, setLabel] = useState("");
  const [sub, setSub] = useState({ id: "", label: "" });
  return (
    <div className="space-y-6">
      <BrandingReviews onCount={onCount} />
      <div className="w-full sm:w-80"><AsyncCombobox value={cid} selectedLabel={label} onChange={(id, item) => { setCid(id); setLabel(item?.label || ""); setSub({ id: "", label: "" }); }} fetcher={searchCustomers} placeholder="Choose customer" emptyText="No customers found." testId="branding-customer-picker" /></div>
      {cid && <SubsidiaryPicker cid={cid} value={sub.id} label={sub.label} onChange={(id, l) => setSub({ id, label: l })} />}
      <LabelBranding key={sub.id || cid} cid={sub.id || cid} />
    </div>
  );
}

export default function WelcomeLabelStudio() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "library";
  const [pending, setPending] = useState(0);
  useEffect(() => { api.get("/welcome-label/branding-reviews").then((r) => setPending(r.data.items.length)).catch(() => {}); }, []);
  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-8" data-testid="welcome-label-studio">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-teal-700 text-white"><Gift className="h-5 w-5" /></div>
        <div><h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Welcome Labels &amp; Tips</h1>
          <p className="text-sm text-slate-500">Review device tips, approve translations and customer branding, edit what every label says, and see which tips people engage with.</p></div>
      </div>
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v })}>
        <TabsList className="max-w-full justify-start overflow-x-auto" data-testid="welcome-studio-tabs">
          <TabsTrigger value="direct-sales" data-testid="tab-direct-sales">Direct sale / Gift labels</TabsTrigger>
          <TabsTrigger value="library" data-testid="tab-library">Tip library</TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights">Engagement insights</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">Customer branding{pending > 0 && <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white" data-testid="tab-branding-pending">{pending}</span>}</TabsTrigger>
          <TabsTrigger value="content" data-testid="tab-content">Label content</TabsTrigger>
          <TabsTrigger value="offers" data-testid="tab-offers">Seasonal offers</TabsTrigger>
          <TabsTrigger value="voice" data-testid="tab-voice">Employee voice</TabsTrigger>
          <TabsTrigger value="switches" data-testid="tab-switches">Experience switches</TabsTrigger>
        </TabsList>
        <TabsContent value="direct-sales" className="mt-5"><DirectSaleLabels /></TabsContent>
        <TabsContent value="library" className="mt-5"><TipLibrary /></TabsContent>
        <TabsContent value="insights" className="mt-5"><TipInsights /></TabsContent>
        <TabsContent value="branding" className="mt-5"><BrandingTab onCount={setPending} /></TabsContent>
        <TabsContent value="content" className="mt-5"><LabelContent /></TabsContent>
        <TabsContent value="offers" className="mt-5"><PromotionsTab /></TabsContent>
        <TabsContent value="voice" className="mt-5"><EmployeeVoice /></TabsContent>
        <TabsContent value="switches" className="mt-5"><ExperienceSwitches /></TabsContent>
      </Tabs>
    </div>
  );
}
